/**
 * Hero 轮播编辑器组件
 */
import React, { useState, useEffect } from 'react';
import { Button, Table, Modal, Form, Input, InputNumber, Switch, Space, Tag, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import FileUploader from '../FileUploader';

interface HeroSlide {
  id: string;
  image_url: string;
  title: string;
  subtitle: string;
  slogan: string;
  link_url: string;
  link_text: string;
  sort_order: number;
  is_active: boolean;
}

interface HeroSlidesEditorProps {
  data: HeroSlide[];
  onSave: (slides: HeroSlide[]) => Promise<void>;
}

const HeroSlidesEditor: React.FC<HeroSlidesEditorProps> = ({ data, onSave }) => {
  const [slides, setSlides] = useState<HeroSlide[]>(data || []);
  const [editModal, setEditModal] = useState<HeroSlide | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSlides(data || []);
  }, [data]);

  const doSave = async (updatedSlides: HeroSlide[]) => {
    setSaving(true);
    try {
      await onSave(updatedSlides);
      setSlides(updatedSlides);
    } catch (e: any) {
      message.error('保存失败: ' + (e?.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = () => {
    setEditModal({
      id: `hero-${Date.now()}`,
      image_url: '',
      title: '',
      subtitle: '',
      slogan: '',
      link_url: '/register',
      link_text: '立即体验',
      sort_order: slides.length + 1,
      is_active: true,
    });
  };

  const handleModalOk = async () => {
    if (!editModal) return;
    if (!editModal.image_url) { message.warning('请上传背景图片'); return; }
    if (!editModal.title) { message.warning('请填写标题'); return; }

    let updatedSlides: HeroSlide[];
    if (slides.find(s => s.id === editModal.id)) {
      updatedSlides = slides.map(s => s.id === editModal.id ? editModal : s);
    } else {
      updatedSlides = [...slides, editModal];
    }
    setEditModal(null);
    await doSave(updatedSlides);
  };

  const handleDelete = async (id: string) => {
    const updatedSlides = slides.filter(s => s.id !== id);
    await doSave(updatedSlides);
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newSlides = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newSlides.length) return;
    [newSlides[index], newSlides[targetIndex]] = [newSlides[targetIndex], newSlides[index]];
    const reordered = newSlides.map((s, i) => ({ ...s, sort_order: i + 1 }));
    await doSave(reordered);
  };

  const columns: ColumnsType<HeroSlide> = [
    { title: 'Slogan', dataIndex: 'slogan', width: 200,
      render: (v: string) => v || <span style={{ color: '#999' }}>未填写</span> },
    { title: '标题', dataIndex: 'title', width: 180,
      render: (v: string) => <span style={{ whiteSpace: 'pre-line' }}>{v?.substring(0, 40)}</span> },
    { title: '副标题', dataIndex: 'subtitle',
      render: (html: string) => (
        <div style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666' }}>
          {html || '—'}
        </div>
      ),
    },
    { title: '排序', dataIndex: 'sort_order', width: 70 },
    { title: '状态', dataIndex: 'is_active', width: 80,
      render: (active: boolean) => active ? <Tag color="green">启用</Tag> : <Tag>禁用</Tag>,
    },
    {
      title: '操作', width: 200,
      render: (_, record, index) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0 || saving}
            onClick={() => handleMove(index, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === slides.length - 1 || saving}
            onClick={() => handleMove(index, 'down')} />
          <Button size="small" onClick={() => setEditModal(record)}>编辑</Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)} loading={saving}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, color: '#666', fontSize: 13 }}>
        每张幻灯片对应一张图片和一段 Hero 文字，前台以轮播形式展示。添加、编辑或删除后自动保存。
      </div>
      <Button type="primary" onClick={handleAdd} style={{ marginBottom: 16 }} loading={saving}>
        添加幻灯片
      </Button>
      <Table dataSource={slides} columns={columns} rowKey="id" pagination={false} loading={saving} />

      <Modal
        title="编辑 Hero 幻灯片"
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleModalOk}
        confirmLoading={saving}
        width={800}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="背景图片（必填）">
            <FileUploader
              category="image"
              value={editModal?.image_url ? [editModal.image_url] : []}
              onChange={(urls) => setEditModal(editModal ? { ...editModal, image_url: urls[0] || '' } : null)}
              maxCount={1}
              useAdminAPI={true}
            />
            {editModal?.image_url && (
              <div style={{ marginTop: 8, color: '#52c41a', fontSize: 12 }}>
                ✓ 已上传: {editModal.image_url}
              </div>
            )}
          </Form.Item>
          <Form.Item label="标题（必填，回车换行，**文字** 显示金色）">
            <Input.TextArea
              value={editModal?.title}
              onChange={(e) => setEditModal(editModal ? { ...editModal, title: e.target.value } : null)}
              placeholder={"精准给药，\n**AI 驱动**个体化治疗"}
              rows={3}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          <Form.Item label="图片下方 Slogan">
            <Input
              value={editModal?.slogan || ''}
              onChange={(e) => setEditModal(editModal ? { ...editModal, slogan: e.target.value } : null)}
              placeholder="如：MIPD · PopPK · Bayesian Estimation"
            />
          </Form.Item>
          <Form.Item label="副标题（支持 Markdown 格式）">
            <Input.TextArea
              value={editModal?.subtitle || ''}
              onChange={(e) => setEditModal(editModal ? { ...editModal, subtitle: e.target.value } : null)}
              placeholder="支持 Markdown：**粗体** *斜体* [链接](url)"
              rows={4}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>
          <Form.Item label="按钮链接">
            <Input
              value={editModal?.link_url}
              onChange={(e) => setEditModal(editModal ? { ...editModal, link_url: e.target.value } : null)}
              placeholder="/register 或 https://example.com"
            />
          </Form.Item>
          <Form.Item label="按钮文本">
            <Input
              value={editModal?.link_text}
              onChange={(e) => setEditModal(editModal ? { ...editModal, link_text: e.target.value } : null)}
              placeholder="立即体验"
            />
          </Form.Item>
          <Form.Item label="排序">
            <InputNumber
              value={editModal?.sort_order}
              onChange={(v) => setEditModal(editModal ? { ...editModal, sort_order: v || 1 } : null)}
              min={1}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="状态">
            <Switch
              checked={editModal?.is_active}
              onChange={(v) => setEditModal(editModal ? { ...editModal, is_active: v } : null)}
              checkedChildren="启用"
              unCheckedChildren="禁用"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HeroSlidesEditor;
